import { Fragment, useCallback, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  Button,
  IconButton,
  Paper,
  Stack,
  Switch,
  Popover,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import {
  MoreHoriz as MoreHorizIcon,
  OfflineBolt as OfflineBoltIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from '@mui/icons-material';
import { Menu, useFlowBuilder2Context, useEditorStore } from '@diana/core';
import { useFindActiveTask } from '../../../Support/useFindActiveTask';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { useDeleteActivity } from '../../../Support/useDeleteActivity';
import { FlowBuilderModalContentTypeEnum } from '../../../Store/FlowBuilder2PageStore.types';

export const ConfigureActionActivities = (props: { disabled: boolean; actionIndex: number }) => {
  const { disabled, actionIndex } = props;
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette } = useTheme();
  const findActiveTask = useFindActiveTask();

  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<HTMLElement | null>(null);

  const setActiveModal = useFlowBuilder2PageStore((s) => s.setActiveModal);

  const taskId = useEditorStore((s) => findActiveTask(s.flow)?.taskId ?? '');
  const actionSetId = useEditorStore((s) => findActiveTask(s.flow)?.actionSetId ?? '');
  const actionId = useEditorStore((s) => findActiveTask(s.flow)?.actions[actionIndex]?.id ?? '');
  const newActivityIndex = useEditorStore((s) => {
    const activities = findActiveTask(s.flow)?.actions[actionIndex]?.activities ?? [];
    return activities.length;
  });
  const activities = useEditorStore((s) => {
    const activities = findActiveTask(s.flow)?.actions[actionIndex]?.activities ?? [];
    const items = activities.map(({ id, name }, index) => ({ id, name, index }));
    return items.filter((item) => item.name === 'UpdateAssetValueActivity');
  }, isEqual);

  const onAddUpdateAssetValueActivity = useCallback(() => {
    setAddMenuAnchorEl(null);
    setActiveModal({
      name: FlowBuilderModalContentTypeEnum.UpdateAssetValue,
      context: {
        taskId,
        actionSetId,
        actionId,
        activityIndex: newActivityIndex,
      },
    });
  }, [setActiveModal, taskId, actionSetId, actionId, newActivityIndex]);

  if (disabled) {
    return <Fragment />;
  }

  return (
    <Fragment>
      <Stack gap={2}>
        {activities.map((activity) => (
          <ActivityItem
            key={activity.id + activity.index}
            actionIndex={actionIndex}
            activityIndex={activity.index}
          />
        ))}

        <Button
          onClick={(e) => setAddMenuAnchorEl(e.currentTarget)}
          color="secondary"
          sx={{
            gap: 1,
            justifyContent: 'flex-start',
            '&:hover': { backgroundColor: palette.action.hover },
          }}
        >
          <AddCircleOutlineIcon sx={{ color: palette.text.secondary, width: 20, height: 20 }} />
          <Typography color={palette.text.primary}>{t('labels.addActivity')}</Typography>
        </Button>
      </Stack>

      <Popover
        open={Boolean(addMenuAnchorEl)}
        anchorEl={addMenuAnchorEl}
        transitionDuration={0}
        onClose={() => setAddMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {addMenuAnchorEl && (
          <MenuItem
            sx={{ width: addMenuAnchorEl.clientWidth }}
            onClick={onAddUpdateAssetValueActivity}
          >
            {t('labels.updateAssetValue')}
          </MenuItem>
        )}
      </Popover>
    </Fragment>
  );
};

const ActivityItem = (props: { actionIndex: number; activityIndex: number }) => {
  const { actionIndex, activityIndex } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const { palette } = useTheme();
  const { updateAndSaveActionSetById } = useFlowBuilder2Context();

  const findActiveTask = useFindActiveTask();

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const setActiveModal = useFlowBuilder2PageStore((s) => s.setActiveModal);

  const taskId = useEditorStore((s) => findActiveTask(s.flow)?.taskId ?? '');
  const actionSetId = useEditorStore((s) => findActiveTask(s.flow)?.actionSetId ?? '');
  const actionId = useEditorStore((s) => findActiveTask(s.flow)?.actions[actionIndex]?.id ?? '');

  const activity = useEditorStore((s) => {
    return findActiveTask(s.flow)?.actions[actionIndex]?.activities?.[activityIndex] ?? null;
  }, isEqual);

  const { deleteActivity, isDeletingActivity } = useDeleteActivity();

  const enabled = useMemo(() => {
    return !!activity?.properties.enable;
  }, [activity?.properties.enable]);

  const setEnabled = useCallback(
    (value: boolean) => {
      updateAndSaveActionSetById(actionSetId, (draft) => {
        const draftAction = draft.actions[actionIndex];
        if (draftAction && draftAction.activities) {
          const draftActivity = draftAction.activities[activityIndex];
          if (draftActivity) {
            draftActivity.properties.enable = value;
          }
        }
        return draft;
      });
    },
    [actionIndex, actionSetId, activityIndex, updateAndSaveActionSetById],
  );

  if (!activity) {
    return null;
  }

  return (
    <>
      <Paper sx={{ minHeight: '55px' }}>
        <Stack direction="row" p={1} justifyContent="space-between" alignItems="center">
          <OfflineBoltIcon
            sx={({ palette }) => ({
              mr: 1,
              color: enabled ? palette.primary.main : palette.secondary.main,
            })}
          />
          <Typography width="100%">{activity.displayName}</Typography>
          <Switch
            sx={{ m: -1.5 }}
            checked={enabled}
            onChange={(_event, value) => setEnabled(value)}
          />
          <IconButton
            sx={{ ml: '10px' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuAnchorEl(e.currentTarget);
            }}
          >
            <MoreHorizIcon />
          </IconButton>
        </Stack>
      </Paper>
      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
        actions={[
          {
            title: t('buttons.edit'),
            onClick: () => {
              setMenuAnchorEl(null);
              setActiveModal({
                name: FlowBuilderModalContentTypeEnum.UpdateAssetValue,
                context: {
                  taskId,
                  actionSetId,
                  actionId,
                  activityIndex,
                },
              });
            },
          },
          {
            title: t('buttons.delete'),
            color: palette.error.main,
            loading: isDeletingActivity,
            onClick: async () => {
              deleteActivity({
                actionSetId,
                actionId,
                activityId: activity.id,
              })
                .catch()
                .finally(() => {
                  setMenuAnchorEl(null);
                });
            },
          },
        ]}
      />
    </>
  );
};
